import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Master HTML wrapper template for portal Job Description downloads.
 *
 * CRM staff paste one HTML document (with {{tokens}}) on the Job Description
 * Templates ▸ HTML Template tab. When a portal client downloads a job
 * description as a PDF or Word doc, the tokens are swapped for that client's
 * branding plus the document's title/content, and the result is rendered. When
 * no template is saved the portal falls back to its built-in layout.
 */
const templateRef = () => doc(db, 'jobDescriptionHtmlTemplate', 'data');

/** Tokens available in the JD HTML template (drives the CRM guide UI). */
export const JD_HTML_TOKENS = [
  { token: 'logoUrl', label: "Client's company logo", required: true, hint: 'Image URL — must be used inside an <img> tag, e.g. <img src="{{logoUrl}}">' },
  { token: 'title', label: 'Job title', required: true, hint: 'Plain text — the title of the job description being downloaded' },
  { token: 'body', label: 'Job description content', required: true, hint: 'The full document as rendered HTML (headings, paragraphs, lists). Place it where the document body should go.' },
  { token: 'department', label: 'Department', required: false, hint: 'Plain text — Design, Construction or Office' },
  { token: 'companyName', label: "Client's company name", required: false, hint: 'Plain text' },
  { token: 'primaryColor', label: 'Primary brand color', required: false, hint: 'Hex code — use in style attributes, e.g. style="color:{{primaryColor}}"' },
  { token: 'secondaryColor', label: 'Secondary brand color', required: false, hint: 'Hex code — use in style attributes' },
  { token: 'date', label: "Today's date", required: false, hint: 'Plain text, e.g. "July 10, 2026"' },
];

/** Basic starter: centered logo, title, then the document content. */
export const DEFAULT_JD_HTML_TEMPLATE = `<div style="text-align: center; margin-bottom: 24px;">
  <img src="{{logoUrl}}" alt="{{companyName}}" style="max-height: 80px; max-width: 260px;" />
</div>
<h1 style="text-align: center; font-size: 28px; font-weight: bold; color: #111827; margin: 0 0 24px;">{{title}}</h1>
{{body}}`;

/** @returns {Promise<{html: string}>} html is '' when no template is saved. */
export async function fetchJdHtmlTemplate() {
  try {
    const snap = await getDoc(templateRef());
    if (snap.exists()) return { html: snap.data().html || '' };
  } catch (err) {
    console.error('Error fetching Job Description HTML template:', err);
  }
  return { html: '' };
}

/** Save the master template. An empty string means "use the built-in layout". */
export async function saveJdHtmlTemplate(html) {
  await setDoc(templateRef(), {
    html: html || '',
    updatedAt: serverTimestamp(),
  });
}

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Replace every {{token}} in the template. `body` is inserted as raw HTML (it
 * IS markup); every other value is escaped plain text. Unknown tokens collapse
 * to ''. `date` defaults to today when not supplied.
 */
export function renderJdHtmlTemplate(templateHtml = '', values = {}) {
  const withDate = {
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    ...values,
  };
  return templateHtml.replace(TOKEN_PATTERN, (_, key) => {
    if (key === 'body') return withDate.body ?? '';
    return escapeHtml(withDate[key] ?? '');
  });
}
